import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Connector, CONNECTOR_TYPE_LABELS } from "@/types";
import { getPartnerTypeColor } from "./partnerMarkers";
import { ChevronRight, MapPinOff } from "lucide-react";

interface UnmappedPartnersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partners: Connector[];
}

/**
 * Lists partners that have no saved coordinates, so they can be chased up and
 * added to the map. Excluded from the map itself rather than approximated.
 */
export function UnmappedPartnersSheet({
  open,
  onOpenChange,
  partners,
}: UnmappedPartnersSheetProps) {
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <MapPinOff className="h-5 w-5 text-muted-foreground" />
            Unmapped Partners ({partners.length})
          </SheetTitle>
          <SheetDescription>
            These partners have no geo-location saved, so they cannot be shown
            on the map. Open a partner to set its location.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-4">
          {partners.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Every partner in the current filter has a location. Nice.
            </p>
          ) : (
            <div className="space-y-2 pb-4">
              {partners.map((partner) => (
                <button
                  key={partner.id}
                  onClick={() =>
                    navigate(`/tools/channel-partners/${partner.id}`)
                  }
                  className="w-full text-left p-3 rounded-lg border hover:bg-secondary/60 transition-colors flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {partner.name}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge
                        variant="secondary"
                        className="text-[10px] font-medium border-0 text-white"
                        style={{
                          backgroundColor: getPartnerTypeColor(partner.type),
                        }}
                      >
                        {CONNECTOR_TYPE_LABELS[partner.type] ?? partner.type}
                      </Badge>
                      <span className="text-xs text-muted-foreground truncate">
                        {[partner.city, partner.state]
                          .filter(Boolean)
                          .join(", ") || "No city set"}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
